import {
  CheckCircleOutline as CheckAllIcon,
  Close as CloseIcon,
  RadioButtonUnchecked as ClearAllIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  useGetUsersWithRoleQuery,
  UserRoleType,
} from "../../../store/services/userService";

interface ActiveSalespeopleDrawerProps {
  open: boolean;
  onClose: () => void;
  activeUserNos: number[];
  onToggle: (id: number) => void;
  onSetAll: (ids: number[]) => void;
  onClear: () => void;
}

const ActiveSalespeopleDrawer: React.FC<ActiveSalespeopleDrawerProps> = ({
  open,
  onClose,
  activeUserNos,
  onToggle,
  onSetAll,
  onClear,
}) => {
  const theme = useTheme();
  const { data: salespeople, isLoading } = useGetUsersWithRoleQuery(
    UserRoleType.SalesPerson
  );
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!salespeople) return [];
    const q = search.trim().toLowerCase();
    const sorted = [...salespeople].sort((a, b) =>
      a.Name.localeCompare(b.Name, "sv")
    );
    if (!q) return sorted;
    return sorted.filter((person) => person.Name.toLowerCase().includes(q));
  }, [salespeople, search]);

  const selectedSet = useMemo(() => new Set(activeUserNos), [activeUserNos]);
  const totalCount = salespeople?.length ?? 0;
  const selectedCount = activeUserNos.length;

  const handleSelectAll = () => {
    if (!salespeople) return;
    onSetAll(salespeople.map((p) => p.Id));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Välj aktiva säljare
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Markera de säljare som ska visas i dashboarden. Valet sparas per
              företag.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <TextField
          fullWidth
          size="small"
          placeholder="Sök säljare..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckAllIcon />}
            onClick={handleSelectAll}
            disabled={isLoading || totalCount === 0}
          >
            Markera alla
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<ClearAllIcon />}
            onClick={onClear}
            disabled={selectedCount === 0}
          >
            Rensa
          </Button>
        </Stack>
      </Box>

      {/* Lista */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Laddar säljare...
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {search ? "Inga träffar på sökningen." : "Inga säljare hittades."}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filtered.map((person) => {
              const checked = selectedSet.has(person.Id);
              return (
                <ListItemButton
                  key={person.Id}
                  onClick={() => onToggle(person.Id)}
                  sx={{
                    py: 1,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={checked}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Avatar
                      src={person.ImagePath}
                      sx={{ width: 32, height: 32 }}
                    >
                      {person.Name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={person.Name}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: checked ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Divider />
      <Box
        sx={{
          p: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>{selectedCount}</strong> av <strong>{totalCount}</strong>{" "}
          säljare valda
          {selectedCount === 0 && " — alla visas som fallback"}
        </Typography>
      </Box>
    </Drawer>
  );
};

export default ActiveSalespeopleDrawer;
